/**
 * MessageMarkdown Component
 *
 * Main markdown renderer for chat messages following Mantine UI best practices.
 * Integrates with the existing chat system to provide rich text rendering
 * with citation and follow-up question support.
 */

import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkMathExtended from './config/remarkMathExtended';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { Box } from '@mantine/core';
import { sanitizeSchema } from './config/sanitizeSchema';
import { createMarkdownComponents } from './config/markdownConfig';
import { preprocessContent } from './processors/contentPreprocessor';
import classes from './MessageMarkdown.module.css';
import type { MessageMarkdownProps, Citation } from './types';

export function MessageMarkdown({
  content,
  onCitationClick,
  onFollowUpClick,
  onCitationsExtracted,
  className,
  enableImageDownload,
  skipEnhancements = false,
}: MessageMarkdownProps) {
  // Track previous citations to prevent unnecessary callbacks
  const previousCitationsRef = useRef<Citation[]>([]);

  // Process both citations and follow-up questions from content
  const { content: processedContent, citations, questions } = useMemo(() => {
    if (skipEnhancements) {
      return { content, citations: [], questions: [] };
    }
    return preprocessContent(content);
  }, [content, skipEnhancements]);

  // Notify parent component about extracted citations (with change detection)
  useEffect(() => {
    if (skipEnhancements || !onCitationsExtracted) return;

    const citationsChanged =
      citations.length !== previousCitationsRef.current.length ||
      citations.some((citation, index) => {
        const prevCitation = previousCitationsRef.current[index];
        return !prevCitation ||
          citation.id !== prevCitation.id ||
          citation.text !== prevCitation.text ||
          citation.url !== prevCitation.url ||
          citation.index !== prevCitation.index;
      });

    if (citationsChanged) {
      // Update ref before calling callback to prevent loop
      previousCitationsRef.current = [...citations];
      onCitationsExtracted(citations);
    }
  }, [citations, onCitationsExtracted, skipEnhancements]);

  // Handle citation clicks (memoized to prevent unnecessary re-renders)
  // NOTE: This component does NOT open URLs itself — it delegates to the
  // parent (AssistantMessage) via onCitationClick which owns the single
  // safelyOpenUrl call.  Opening here would cause duplicate tabs.
  const handleCitationClick = useCallback((citation: Citation) => {
    // Call the parent callback with full citation object
    onCitationClick?.(citation);
  }, [onCitationClick]);

  // Create markdown components with both citation, follow-up, and image handling (memoized)
  const markdownComponents = useMemo(
    () =>
      createMarkdownComponents({
        citations,
        questions,
        onCitationClick: handleCitationClick,
        onFollowUpClick,
        enableImageDownload,
      }),
    [citations, questions, handleCitationClick, onFollowUpClick, enableImageDownload],
  );

  return (
    <Box className={`${classes.container} ${className || ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMathExtended]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema], rehypeKatex]}
        components={markdownComponents}
        className={classes.markdown}
      >
        {processedContent}
      </ReactMarkdown>
    </Box>
  );
};

export default MessageMarkdown;
